import {
  collection,
  doc,
  getDocs,
  getDoc,
  addDoc,
  updateDoc,
  deleteDoc,
  query,
  orderBy,
  where,
  limit,
  startAfter,
  Timestamp,
  onSnapshot,
  Unsubscribe
} from 'firebase/firestore';
import { db } from '../firebase/config';
import {
  Bus,
  BusType,
  BusAmenity,
  BusFeature,
  CreateBusForm,
  UpdateBusForm,
  CreateBusTypeForm,
  UpdateBusTypeForm,
  CreateAmenityForm,
  UpdateAmenityForm,
  CreateFeatureForm,
  UpdateFeatureForm,
  BusStats,
  BusUtilizationData,
  MaintenanceRecord
} from '../types';

export class BusService {
  // Bus Management
  static async getBuses(limitCount: number = 50, startAfterDate?: Date): Promise<Bus[]> {
    try {
      const busesRef = collection(db, 'buses');
      const constraints: any[] = [orderBy('createdAt', 'desc'), limit(limitCount)];
      if (startAfterDate) {
        constraints.push(startAfter(startAfterDate));
      }
      const q = query(busesRef, ...constraints);
      const snapshot = await getDocs(q);
      return this.processBusSnapshots(snapshot.docs);
    } catch (error: any) {
      throw new Error(`Failed to fetch buses: ${error.message}`);
    }
  }

  static subscribeToBuses(
    onUpdate: (buses: Bus[]) => void,
    onError: (error: Error) => void,
    limitCount: number = 50
  ): Unsubscribe {
    const busesRef = collection(db, 'buses');
    const q = query(busesRef, orderBy('createdAt', 'desc'), limit(limitCount));

    return onSnapshot(q, async (snapshot) => {
      try {
        const buses = await this.processBusSnapshots(snapshot.docs);
        onUpdate(buses);
      } catch (error: any) {
        onError(new Error(`Failed to process bus updates: ${error.message}`));
      }
    }, (error) => {
      onError(new Error(`Snapshot listener error: ${error.message}`));
    });
  }

  private static async processBusSnapshots(docs: any[]): Promise<Bus[]> {
    const buses = await Promise.all(
      docs.map(async (doc) => {
        const data = doc.data();
        // Use simpler lookups or cache for performance in real-time listeners if possible
        // For now, keeping full resolution to ensure data integrity
        const busType = await this.getBusTypeById(data.busTypeId);
        const amenities = await this.getAmenitiesByIds(data.amenityIds || []);
        const features = await this.getFeaturesByIds(data.featureIds || []);

        const defaultBusType: BusType = {
          id: data.busTypeId || 'unknown',
          name: 'Unknown Type',
          description: 'No description available',
          basePriceMultiplier: 1,
          defaultSeats: data.totalSeats || 0,
          defaultAmenities: [],
          defaultFeatures: [],
          isActive: false,
          createdAt: new Date()
        };

        return {
          id: doc.id,
          busNumber: data.busNumber,
          busType: busType || defaultBusType,
          operatorName: data.operatorName,
          totalSeats: data.totalSeats,
          availableSeats: data.availableSeats || data.totalSeats,
          amenities,
          features,
          isActive: data.isActive,
          isInService: data.isInService,
          lastMaintenanceDate: data.lastMaintenanceDate?.toDate(),
          nextMaintenanceDate: data.nextMaintenanceDate?.toDate(),
          mileage: data.mileage,
          fuelType: data.fuelType,
          yearOfManufacture: data.yearOfManufacture,
          registrationNumber: data.registrationNumber,
          insuranceExpiry: data.insuranceExpiry?.toDate(),
          createdAt: data.createdAt?.toDate() || new Date(),
          updatedAt: data.updatedAt?.toDate() || new Date(),
        } as Bus;
      })
    );
    return buses;
  }

  static async getBusById(id: string): Promise<Bus | null> {
    try {
      const busRef = doc(db, 'buses', id);
      const busSnap = await getDoc(busRef);

      if (busSnap.exists()) {
        const data = busSnap.data();
        const busType = await this.getBusTypeById(data.busTypeId);
        const amenities = await this.getAmenitiesByIds(data.amenityIds || []);
        const features = await this.getFeaturesByIds(data.featureIds || []);

        const defaultBusType: BusType = {
          id: data.busTypeId || 'unknown',
          name: 'Unknown Type',
          description: 'No description available',
          basePriceMultiplier: 1,
          defaultSeats: data.totalSeats || 0,
          defaultAmenities: [],
          defaultFeatures: [],
          isActive: false,
          createdAt: new Date()
        };

        return {
          id: busSnap.id,
          busNumber: data.busNumber,
          busType: busType || defaultBusType,
          operatorName: data.operatorName,
          totalSeats: data.totalSeats,
          availableSeats: data.availableSeats || data.totalSeats,
          amenities,
          features,
          isActive: data.isActive,
          isInService: data.isInService,
          lastMaintenanceDate: data.lastMaintenanceDate?.toDate(),
          nextMaintenanceDate: data.nextMaintenanceDate?.toDate(),
          mileage: data.mileage,
          fuelType: data.fuelType,
          yearOfManufacture: data.yearOfManufacture,
          registrationNumber: data.registrationNumber,
          insuranceExpiry: data.insuranceExpiry?.toDate(),
          createdAt: data.createdAt?.toDate() || new Date(),
          updatedAt: data.updatedAt?.toDate() || new Date(),
        } as Bus;
      }
      return null;
    } catch (error: any) {
      throw new Error(`Failed to fetch bus: ${error.message}`);
    }
  }

  static async createBus(busData: CreateBusForm): Promise<Bus> {
    try {
      const busRef = collection(db, 'buses');
      const newBus = {
        ...busData,
        availableSeats: busData.totalSeats,
        isActive: true,
        isInService: true,
        createdAt: Timestamp.now(),
        updatedAt: Timestamp.now(),
      };

      const docRef = await addDoc(busRef, newBus);
      const createdBus = await this.getBusById(docRef.id);
      return createdBus!;
    } catch (error: any) {
      throw new Error(`Failed to create bus: ${error.message}`);
    }
  }

  static async updateBus(id: string, busData: UpdateBusForm): Promise<Bus> {
    try {
      const busRef = doc(db, 'buses', id);
      const updateData = {
        ...busData,
        updatedAt: Timestamp.now(),
      };

      await updateDoc(busRef, updateData);
      const updatedBus = await this.getBusById(id);
      return updatedBus!;
    } catch (error: any) {
      throw new Error(`Failed to update bus: ${error.message}`);
    }
  }

  static async deleteBus(id: string): Promise<void> {
    try {
      const busRef = doc(db, 'buses', id);
      await deleteDoc(busRef);
    } catch (error: any) {
      throw new Error(`Failed to delete bus: ${error.message}`);
    }
  }

  // Bus Types Management
  static async getBusTypes(limitCount: number = 50): Promise<BusType[]> {
    try {
      const busTypesRef = collection(db, 'busTypes');
      const q = query(busTypesRef, orderBy('createdAt', 'desc'), limit(limitCount));
      const snapshot = await getDocs(q);

      return snapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data(),
        createdAt: doc.data().createdAt?.toDate() || new Date(),
      })) as BusType[];
    } catch (error: any) {
      throw new Error(`Failed to fetch bus types: ${error.message}`);
    }
  }

  static async getBusTypeById(id: string): Promise<BusType | null> {
    try {
      const busTypeRef = doc(db, 'busTypes', id);
      const busTypeSnap = await getDoc(busTypeRef);

      if (busTypeSnap.exists()) {
        return {
          id: busTypeSnap.id,
          ...busTypeSnap.data(),
          createdAt: busTypeSnap.data().createdAt?.toDate() || new Date(),
        } as BusType;
      }
      return null;
    } catch (error: any) {
      throw new Error(`Failed to fetch bus type: ${error.message}`);
    }
  }

  static async createBusType(busTypeData: CreateBusTypeForm): Promise<BusType> {
    try {
      const busTypeRef = collection(db, 'busTypes');
      const newBusType = {
        ...busTypeData,
        isActive: true,
        createdAt: Timestamp.now(),
      };

      const docRef = await addDoc(busTypeRef, newBusType);
      const createdBusType = await this.getBusTypeById(docRef.id);
      return createdBusType!;
    } catch (error: any) {
      throw new Error(`Failed to create bus type: ${error.message}`);
    }
  }

  static async updateBusType(id: string, busTypeData: UpdateBusTypeForm): Promise<BusType> {
    try {
      const busTypeRef = doc(db, 'busTypes', id);
      await updateDoc(busTypeRef, busTypeData as any);
      const updatedBusType = await this.getBusTypeById(id);
      return updatedBusType!;
    } catch (error: any) {
      throw new Error(`Failed to update bus type: ${error.message}`);
    }
  }

  // Amenities Management
  static async getAmenities(limitCount: number = 100): Promise<BusAmenity[]> {
    try {
      const amenitiesRef = collection(db, 'amenities');
      const q = query(amenitiesRef, orderBy('name', 'asc'), limit(limitCount));
      const snapshot = await getDocs(q);

      return snapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data(),
      })) as BusAmenity[];
    } catch (error: any) {
      throw new Error(`Failed to fetch amenities: ${error.message}`);
    }
  }

  static async getAmenitiesByIds(ids: string[]): Promise<BusAmenity[]> {
    if (ids.length === 0) return [];

    try {
      const amenities = await Promise.all(
        ids.map(async (id) => {
          const amenityRef = doc(db, 'amenities', id);
          const amenitySnap = await getDoc(amenityRef);
          if (amenitySnap.exists()) {
            return {
              id: amenitySnap.id,
              ...amenitySnap.data(),
            } as BusAmenity;
          }
          return null;
        })
      );

      return amenities.filter(amenity => amenity !== null) as BusAmenity[];
    } catch (error: any) {
      throw new Error(`Failed to fetch amenities: ${error.message}`);
    }
  }

  static async createAmenity(amenityData: CreateAmenityForm): Promise<BusAmenity> {
    try {
      const amenityRef = collection(db, 'amenities');
      const newAmenity = {
        ...amenityData,
        isActive: true,
      };

      const docRef = await addDoc(amenityRef, newAmenity);
      const amenitySnap = await getDoc(docRef);
      return {
        id: docRef.id,
        ...amenitySnap.data(),
      } as BusAmenity;
    } catch (error: any) {
      throw new Error(`Failed to create amenity: ${error.message}`);
    }
  }

  static async updateAmenity(id: string, amenityData: UpdateAmenityForm): Promise<BusAmenity> {
    try {
      const amenityRef = doc(db, 'amenities', id);
      await updateDoc(amenityRef, amenityData as any);
      const amenitySnap = await getDoc(amenityRef);
      return {
        id: amenitySnap.id,
        ...amenitySnap.data(),
      } as BusAmenity;
    } catch (error: any) {
      throw new Error(`Failed to update amenity: ${error.message}`);
    }
  }

  // Features Management
  static async getFeatures(limitCount: number = 100): Promise<BusFeature[]> {
    try {
      const featuresRef = collection(db, 'features');
      const q = query(featuresRef, orderBy('name', 'asc'), limit(limitCount));
      const snapshot = await getDocs(q);

      return snapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data(),
      })) as BusFeature[];
    } catch (error: any) {
      throw new Error(`Failed to fetch features: ${error.message}`);
    }
  }

  static async getFeaturesByIds(ids: string[]): Promise<BusFeature[]> {
    if (ids.length === 0) return [];

    try {
      const features = await Promise.all(
        ids.map(async (id) => {
          const featureRef = doc(db, 'features', id);
          const featureSnap = await getDoc(featureRef);
          if (featureSnap.exists()) {
            return {
              id: featureSnap.id,
              ...featureSnap.data(),
            } as BusFeature;
          }
          return null;
        })
      );

      return features.filter(feature => feature !== null) as BusFeature[];
    } catch (error: any) {
      throw new Error(`Failed to fetch features: ${error.message}`);
    }
  }

  static async createFeature(featureData: CreateFeatureForm): Promise<BusFeature> {
    try {
      const featureRef = collection(db, 'features');
      const newFeature = {
        ...featureData,
        isActive: true,
      };

      const docRef = await addDoc(featureRef, newFeature);
      const featureSnap = await getDoc(docRef);
      return {
        id: docRef.id,
        ...featureSnap.data(),
      } as BusFeature;
    } catch (error: any) {
      throw new Error(`Failed to create feature: ${error.message}`);
    }
  }

  static async updateFeature(id: string, featureData: UpdateFeatureForm): Promise<BusFeature> {
    try {
      const featureRef = doc(db, 'features', id);
      await updateDoc(featureRef, featureData as any);
      const featureSnap = await getDoc(featureRef);
      return {
        id: featureSnap.id,
        ...featureSnap.data(),
      } as BusFeature;
    } catch (error: any) {
      throw new Error(`Failed to update feature: ${error.message}`);
    }
  }

  // Statistics
  static async getBusStats(): Promise<BusStats> {
    try {
      const busesRef = collection(db, 'buses');
      const snapshot = await getDocs(busesRef);

      const buses = snapshot.docs.map(doc => doc.data());

      const totalBuses = buses.length;
      const activeBuses = buses.filter(bus => bus.isActive).length;
      const inServiceBuses = buses.filter(bus => bus.isInService).length;
      const maintenanceDue = buses.filter(bus => {
        if (!bus.nextMaintenanceDate) return false;
        const nextMaintenance = bus.nextMaintenanceDate.toDate();
        const today = new Date();
        return nextMaintenance <= today;
      }).length;

      const electricBuses = buses.filter(bus => bus.fuelType === 'electric').length;
      const dieselBuses = buses.filter(bus => bus.fuelType === 'diesel').length;
      const hybridBuses = buses.filter(bus => bus.fuelType === 'hybrid').length;

      const totalMileage = buses.reduce((sum, bus) => sum + (bus.mileage || 0), 0);
      const averageMileage = totalBuses > 0 ? totalMileage / totalBuses : 0;

      // Calculate utilization rate (simplified)
      const utilizationRate = totalBuses > 0 ? (inServiceBuses / totalBuses) * 100 : 0;

      return {
        totalBuses,
        activeBuses,
        inServiceBuses,
        maintenanceDue,
        electricBuses,
        dieselBuses,
        hybridBuses,
        averageMileage,
        utilizationRate,
      };
    } catch (error: any) {
      throw new Error(`Failed to fetch bus stats: ${error.message}`);
    }
  }

  // Bus Utilization
  static async getBusUtilization(): Promise<BusUtilizationData[]> {
    try {
      // This would typically involve complex queries across bookings and routes
      // For now, return mock data
      return [];
    } catch (error: any) {
      throw new Error(`Failed to fetch bus utilization: ${error.message}`);
    }
  }

  // Maintenance Records
  static async getMaintenanceRecords(busId?: string): Promise<MaintenanceRecord[]> {
    try {
      const maintenanceRef = collection(db, 'maintenanceRecords');
      let q = query(maintenanceRef, orderBy('performedAt', 'desc'));

      if (busId) {
        q = query(q, where('busId', '==', busId));
      }

      const snapshot = await getDocs(q);
      return snapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data(),
        performedAt: doc.data().performedAt?.toDate() || new Date(),
        nextDueDate: doc.data().nextDueDate?.toDate(),
      })) as MaintenanceRecord[];
    } catch (error: any) {
      throw new Error(`Failed to fetch maintenance records: ${error.message}`);
    }
  }
}
